import { css } from "@emotion/react";
import React, { forwardRef } from "react";
import { ShimmerEffect } from "../ShimmerEffect";
import { useTheme } from "../theme";
import { InputAdornment } from "./InputAdornment";

export interface InputBaseProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * 入力欄内部前方に装飾を挿入します。
   */
  startAdornment?: React.ReactNode;
  /**
   * 入力欄内部後方に装飾を挿入します。
   */
  endAdornment?: React.ReactNode;
  /**
   * 挿入される値がloading中であることを表します。
   */
  loading?: boolean;
  children?: never;
  renderInputWrapper?: ({
    children,
  }: {
    children: React.ReactNode;
  }) => React.JSX.Element;
  "data-touched"?: string | boolean;
  "data-invalid"?: string | boolean;
}

const DefaultInputWrapper = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

export const InputBase = forwardRef<HTMLInputElement, InputBaseProps>(
  (
    {
      startAdornment,
      endAdornment,
      style,
      className,
      loading,
      renderInputWrapper: InputWrapper = DefaultInputWrapper,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    return (
      <ShimmerEffect active={loading}>
        <div
          aria-readonly={props.readOnly}
          aria-disabled={props.disabled}
          data-touched={props["data-touched"]}
          data-invalid={props["data-invalid"]}
          style={style}
          className={className}
          css={[
            theme.typography.body,
            css`
              position: relative;
              display: flex;
              align-items: center;
              height: 42px;
              overflow: hidden;
              color: ${theme.palette.text.primary};
              background-color: white;
              border: 1px solid ${theme.palette.text.secondary};
              border-radius: 4px;
              transition: all 0.3s;
              &[data-touched="true"][data-invalid="true"] {
                border-color: ${theme.palette.intentions.error.main};
              }
              &[aria-readonly="true"] {
                cursor: text;
                background-color: ${theme.palette.action.readOnlyBackground};
                border-color: ${theme.palette.action.readOnly};
              }
              &:focus-within {
                border-color: ${theme.palette.intentions.primary.main};
              }
              &[aria-disabled="true"] {
                color: ${theme.palette.text.disabled};
                cursor: not-allowed;
                background-color: ${theme.palette.action.disabledBackground};
                border-color: ${theme.palette.action.disabled};
              }
            `,
          ]}
        >
          {loading ? null : (
            <>
              <InputAdornment
                position="start"
                css={css`
                  padding-left: 12px;
                `}
              >
                {startAdornment}
              </InputAdornment>
              <div
                css={css`
                  position: relative;
                  display: grid;
                  flex: 1;
                  height: 100%;
                  margin-right: ${endAdornment === undefined ? 12 : 0}px;
                  margin-left: ${startAdornment === undefined ? 12 : 0}px;
                `}
              >
                <InputWrapper>
                  <input
                    ref={ref}
                    css={css`
                      box-sizing: border-box;
                      display: flex;
                      width: 100%;
                      height: 100%;
                      padding: 0px;
                      font: inherit;
                      color: inherit;
                      letter-spacing: inherit;
                      resize: none;
                      background-color: transparent;
                      border: none;
                      border-radius: inherit;
                      outline: 0px;
                      appearance: none;
                      &::placeholder {
                        color: ${theme.palette.text.secondary};
                      }
                      &::-webkit-calendar-picker-indicator {
                        display: none;
                      }
                      &::-webkit-list-button {
                        display: none;
                      }
                      &:-webkit-autofill {
                        box-shadow: 0 0 0 1000px white inset;
                      }
                      &[type="number"]::-webkit-inner-spin-button {
                        appearance: none;
                      }
                    `}
                    {...props}
                  />
                </InputWrapper>
              </div>
              <InputAdornment
                position="end"
                css={css`
                  padding-right: 12px;
                `}
              >
                {endAdornment}
              </InputAdornment>
            </>
          )}
        </div>
      </ShimmerEffect>
    );
  },
);
